import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Breadcrumb, Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import FileSaver from 'file-saver';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';

import AuthService from "../../services/auth.service";
import ElasticSearchService from "../../services/elasticSearch.service";
import ACService from "../../helpers/accessValidation.js";
import * as Constants from "../../components/constants";

function formatBytes(bytes) {
	if (!bytes && bytes !== 0) return "";
	const units = ["B", "KB", "MB", "GB"];
	let value = Number(bytes);
	let unitIndex = 0;
	while (value >= 1024 && unitIndex < units.length - 1) {
		value /= 1024;
		unitIndex++;
	}
	return (unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)) + " " + units[unitIndex];
}

export default function DatabaseBackup() {
	const navigate = useNavigate();
	const [currentUser, setCurrentUser] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isCreating, setIsCreating] = useState(false);
	const [isRestoring, setIsRestoring] = useState(false);
	const [backups, setBackups] = useState([]);
	const [errors, setErrors] = useState(null);
	const [successful, setSuccessful] = useState(false);
	const [message, setMessage] = useState(null);
	const [lastCreateResult, setLastCreateResult] = useState(null);
	const [restoreFileName, setRestoreFileName] = useState("");
	const [restoreConfirmName, setRestoreConfirmName] = useState("");
	const [includeUsers, setIncludeUsers] = useState(false);
	const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);

	function loadBackups() {
		ElasticSearchService.listDatabaseBackups().then(
			response => {
				setBackups(Array.isArray(response.data) ? response.data : []);
				setIsLoading(false);
			},
			error => {
				setErrors(
					(error.response && error.response.data && error.response.data.message) ||
					error.message ||
					error.toString()
				);
				setIsLoading(false);
			}
		);
	}

	useEffect(() => {
		const user = AuthService.getCurrentUser();
		if (!user) {
			navigate("/login");
			return;
		}
		if (!ACService.accessValidation(user, Constants.AC_ADMIN_NAVIGATION)) {
			setErrors("Sie haben keine Berechtigung, diese Seite aufzurufen.");
			setIsLoading(false);
			return;
		}
		setCurrentUser(user);
		loadBackups();
	}, [navigate]);

	function handleCreateBackup() {
		setIsCreating(true);
		setErrors(null);
		setMessage(null);
		ElasticSearchService.createDatabaseBackup().then(
			response => {
				setLastCreateResult(response.data);
				const hasErrors = response.data.errors && Object.keys(response.data.errors).length > 0;
				setSuccessful(!hasErrors);
				setMessage(hasErrors
					? "Backup " + response.data.fileName + " wurde erstellt, aber nicht alle Indizes konnten exportiert werden."
					: "Backup " + response.data.fileName + " wurde erstellt.");
				setIsCreating(false);
				loadBackups();
			},
			error => {
				setSuccessful(false);
				setMessage(
					(error.response && error.response.data && error.response.data.message) ||
					error.message ||
					error.toString()
				);
				setIsCreating(false);
			}
		);
	}

	function handleDownload(fileName) {
		setErrors(null);
		ElasticSearchService.downloadDatabaseBackup(fileName).then(
			response => {
				FileSaver.saveAs(new Blob([response.data], { type: "application/zip" }), fileName);
			},
			error => {
				setSuccessful(false);
				setMessage(
					(error.response && error.response.data && error.response.data.message) ||
					error.message ||
					error.toString()
				);
			}
		);
	}

	function openRestoreDialog(fileName) {
		setRestoreFileName(fileName);
		setRestoreConfirmName("");
		setIncludeUsers(false);
		setRestoreDialogOpen(true);
	}

	function handleRestore() {
		if (restoreConfirmName !== restoreFileName) {
			return;
		}
		setIsRestoring(true);
		setErrors(null);
		setMessage(null);
		ElasticSearchService.restoreDatabaseBackup(restoreFileName, includeUsers).then(
			response => {
				const restored = response.data && response.data.restored ? response.data.restored : {};
				const errorList = response.data && response.data.errors ? response.data.errors : [];
				const counts = Object.keys(restored).map(indexName => indexName + ": " + restored[indexName]).join(", ");
				setSuccessful(errorList.length === 0);
				setMessage(
					"Backup " + restoreFileName + " wurde eingespielt" +
					(counts ? " (" + counts + ")" : ".") +
					(errorList.length ? " Hinweise: " + errorList.slice(0, 5).join("; ") : "")
				);
				setIsRestoring(false);
				setRestoreDialogOpen(false);
			},
			error => {
				setSuccessful(false);
				setMessage(
					(error.response && error.response.data && error.response.data.message) ||
					error.message ||
					error.toString()
				);
				setIsRestoring(false);
			}
		);
	}

	if (!currentUser) {
		return (
			<Container fluid="xxl">
				<Row>
					<Col>
						<Breadcrumb className="breadcrumb-style">
							<Breadcrumb.Item href="/home">Home</Breadcrumb.Item>
							<Breadcrumb.Item className="breadcrumb-item-style" active>Administration</Breadcrumb.Item>
							<Breadcrumb.Item className="breadcrumb-item-style" active>Datenbank-Backup</Breadcrumb.Item>
						</Breadcrumb>
					</Col>
				</Row>
				<Row>
					<Col>
						<h3>Datenbank-Backup</h3>
					</Col>
				</Row>
				<Row>
					<Col>
						{errors ? (
							<div className="alert alert-danger" role="alert">{errors}</div>
						) : (
							<div>Seite wird geladen…</div>
						)}
					</Col>
				</Row>
			</Container>
		);
	}

	return (
		<Container fluid="xxl">
			<Row>
				<Col>
					<Breadcrumb className="breadcrumb-style">
						<Breadcrumb.Item href="/home">Home</Breadcrumb.Item>
						<Breadcrumb.Item className="breadcrumb-item-style" href="/dashboard">Administration</Breadcrumb.Item>
						<Breadcrumb.Item className="breadcrumb-item-style" active>Datenbank-Backup</Breadcrumb.Item>
					</Breadcrumb>
				</Col>
			</Row>
			<Row>
				<Col>
					<h3>Datenbank-Backup</h3>
					<p>
						Es werden die Dokumente der verbundenen Elasticsearch exportiert
						(Friedhöfe, Grabsteine, Personen, Auswahllisten, Hersteller, Benutzer und weitere Indizes).
						Bilder und Dateien auf dem Dateisystem sind nicht enthalten.
					</p>
				</Col>
			</Row>
			<Row className="text-spacer">
				<Col>
					<Button variant="contained" className="btn btn-primary" onClick={handleCreateBackup} disabled={isCreating || isRestoring}>
						{isCreating ? "Backup wird erstellt…" : "Backup erstellen"}
					</Button>
				</Col>
			</Row>
			{message && (
				<Row className="text-spacer">
					<Col>
						<div className={successful ? "alert alert-success" : "alert alert-danger"} role="alert">{message}</div>
					</Col>
				</Row>
			)}
			{lastCreateResult && lastCreateResult.indices && (
				<Row className="text-spacer">
					<Col>
						<strong>Letztes Backup:</strong> {lastCreateResult.fileName}
						{lastCreateResult.elasticsearchUri ? " aus " + lastCreateResult.elasticsearchUri : ""}
						<ul>
							{Object.keys(lastCreateResult.indices).map(indexName => (
								<li key={indexName}>{indexName}: {lastCreateResult.indices[indexName]} Dokumente</li>
							))}
						</ul>
						{lastCreateResult.errors && Object.keys(lastCreateResult.errors).length > 0 && (
							<div className="alert alert-danger" role="alert">
								<strong>Fehler beim Export:</strong>
								<ul>
									{Object.keys(lastCreateResult.errors).map(indexName => (
										<li key={indexName}>{indexName}: {lastCreateResult.errors[indexName]}</li>
									))}
								</ul>
							</div>
						)}
					</Col>
				</Row>
			)}
			<Row className="top-spacer">
				<Col>
					<h4>Vorhandene Backups</h4>
					<Table striped bordered hover>
						<thead>
							<tr>
								<th>Datei</th>
								<th>Datum</th>
								<th>Größe</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{isLoading && (
								<tr>
									<td colSpan="4">Backups werden geladen…</td>
								</tr>
							)}
							{!isLoading && backups.length === 0 && (
								<tr>
									<td colSpan="4">Es sind noch keine Backups vorhanden.</td>
								</tr>
							)}
							{backups.map(backup => (
								<tr key={backup.fileName}>
									<td>{backup.fileName}</td>
									<td>{backup.lastModified}</td>
									<td>{formatBytes(backup.size)}</td>
									<td>
										<Button size="sm" variant="outline-primary" className="me-2" onClick={() => handleDownload(backup.fileName)} disabled={isCreating || isRestoring}>
											Download
										</Button>
										<Button size="sm" variant="outline-danger" onClick={() => openRestoreDialog(backup.fileName)} disabled={isCreating || isRestoring}>
											Restore
										</Button>
									</td>
								</tr>
							))}
						</tbody>
					</Table>
				</Col>
			</Row>

			<Dialog open={restoreDialogOpen} onClose={() => !isRestoring && setRestoreDialogOpen(false)}>
				<DialogTitle>Backup einspielen</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Bestehende Dokumente mit gleicher ID werden überschrieben. Dokumente, die nur in der aktuellen Datenbank stehen, bleiben erhalten.
						Geben Sie den Dateinamen zur Bestätigung ein.
					</DialogContentText>
					<p><strong>{restoreFileName}</strong></p>
					<TextField
						autoFocus
						margin="dense"
						label="Dateiname zur Bestätigung"
						fullWidth
						variant="standard"
						value={restoreConfirmName}
						onChange={(e) => setRestoreConfirmName(e.target.value)}
						disabled={isRestoring}
					/>
					<Form.Check
						className="mt-3"
						type="checkbox"
						id="include-users"
						label="Benutzerkonto-Index users einspielen"
						checked={includeUsers}
						onChange={(e) => setIncludeUsers(e.target.checked)}
						disabled={isRestoring}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRestoreDialogOpen(false)} disabled={isRestoring}>Abbrechen</Button>
					<Button variant="danger" onClick={handleRestore} disabled={isRestoring || restoreConfirmName !== restoreFileName}>
						{isRestoring ? "Wird eingespielt…" : "Backup einspielen"}
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
}
